/**
 * Página de Lista de Clientes
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Layout, Loading, ErrorMessage, SearchBar } from '@components';
import { ListaCliente } from '@components/cliente/ListaCliente';
import { useFetch, useDebounce } from '@hooks';
import clienteController from '@controllers/clienteController';
import { Cliente } from '@types';
import { SessionUtil } from '@utils';
import theme from '@theme';

type OrderBy = 'codigo-asc' | 'codigo-desc' | 'nome-asc' | 'nome-desc';

export const ListaClientePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');
  const [apiClientes, setApiClientes] = useState<Cliente[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 500);
  const lastApiBusca = useRef<string>('');

  const vendedor = SessionUtil.getVendedor();
  const vendCodigo = vendedor?.vendCodigo || '';

  // Carrega clientes iniciais
  const { data: clientesIniciais, isLoading, error, refetch } = useFetch(
    () => vendCodigo ? clienteController.obterClientes(vendCodigo) : Promise.resolve([])
  );

  // Busca na API (usada tanto pelo auto-search quanto pelo botão manual)
  const buscarNaAPI = useCallback(async (termo: string) => {
    if (!termo.trim()) {
      setApiClientes(null);
      lastApiBusca.current = '';
      return;
    }

    // Evita re-buscar o mesmo termo
    if (termo === lastApiBusca.current) return;

    try {
      setSearchLoading(true);
      lastApiBusca.current = termo;
      const data = await clienteController.obterClientesPorBusca(vendCodigo, termo);
      setApiClientes(data);
    } catch (err) {
      console.error('Erro ao buscar na API:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [vendCodigo]);

  // Auto-search: busca na API automaticamente a partir de 3 caracteres
  useEffect(() => {
    if (debouncedSearch.trim().length >= 3) {
      buscarNaAPI(debouncedSearch.trim());
    } else if (debouncedSearch.trim().length === 0) {
      setApiClientes(null);
      lastApiBusca.current = '';
    }
  }, [debouncedSearch, buscarNaAPI]);

  // Busca manual (botão / Enter) - busca independente do tamanho
  const handleSearchAPI = async () => {
    if (!searchTerm.trim()) {
      setApiClientes(null);
      lastApiBusca.current = '';
      refetch();
      return;
    }
    await buscarNaAPI(searchTerm.trim());
  };

  // Usa dados da API se existirem, senão usa dados iniciais
  const clientes = apiClientes || clientesIniciais;

  // Filtra clientes localmente (só quando < 3 caracteres e sem resultados da API)
  const filteredData = React.useMemo(() => {
    if (!clientes) return [];

    // Se tem resultados da API, não filtra localmente
    if (apiClientes) return clientes;

    // Se não tem busca, retorna todos
    if (!debouncedSearch.trim()) return clientes;

    // Filtra localmente (1-2 caracteres)
    return clienteController.filtrarClientes(clientes, debouncedSearch);
  }, [clientes, debouncedSearch, apiClientes]);

  // Ordena clientes
  const sortedData = React.useMemo(() => {
    if (!filteredData) return [];

    const sorted = [...filteredData];
    const [field, direction] = orderBy.split('-') as ['codigo' | 'nome', 'asc' | 'desc'];

    sorted.sort((a, b) => {
      let comparison = 0;

      if (field === 'codigo') {
        comparison = String(a.codigo).localeCompare(String(b.codigo));
      } else {
        comparison = a.nome.localeCompare(b.nome);
      }

      return direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredData, orderBy]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchAPI();
    }
  };

  if (isLoading || searchLoading) {
    return (
      <Layout title="Clientes">
        <Loading message="Carregando clientes..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Clientes">
        <ErrorMessage
          title="Erro ao carregar clientes"
          message={error.message}
          onRetry={refetch}
        />
      </Layout>
    );
  }

  return (
    <Layout title="Clientes" showHeader showFooter footerType="cliente">
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          <SearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              // Se limpou o campo, reseta busca da API
              if (!value.trim()) {
                setApiClientes(null);
                lastApiBusca.current = '';
              }
            }}
            placeholder="Buscar cliente..."
            onKeyPress={handleKeyPress}
          />
          <IconButton
            onClick={handleSearchAPI}
            color="primary"
            disabled={searchLoading}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              borderRadius: 1,
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
        <FormControl sx={{ minWidth: 100 }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="codigo-asc">Código (0-9)</MenuItem>
            <MenuItem value="codigo-desc">Código (9-0)</MenuItem>
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <ListaCliente
        clientes={sortedData}
        isSearching={!!debouncedSearch || !!apiClientes}
      />
    </Layout>
  );
};

export default ListaClientePage;
